import {
  HOSTED_REPORT_MAX_BYTES,
  HOSTED_REPORT_TOKEN_LENGTH,
  type HostedReportState,
} from '../types/hostedReport.js';

// Tools → HTML Hosting — the pure rules, shared by client and server so the
// upload form and the API can't disagree about what is acceptable, and so the
// viewer and the management list can't disagree about what "expired" means.

/**
 * Lifecycle state, evaluated at READ time against `nowMs` — there is no expiry
 * cron and no stored "expired" flag (the acknowledgement-expiry precedent).
 *
 * ⚠️ REVOKED WINS OVER EXPIRED. A revoked link was deliberately killed by a
 * human; telling its holder "this expired on <date>" would misdescribe why they
 * can't see it, and would imply an extension would help.
 */
export function classifyHostedReportState(
  /** `expiresAtMs: null` = the link never expires. */
  report: { revokedAtMs: number | null; expiresAtMs: number | null },
  nowMs: number,
): HostedReportState {
  // ⚠️ Revoked FIRST, unconditionally — a never-expiring link can still be
  // revoked, and revoking is the only way such a link stops working.
  if (report.revokedAtMs !== null) return 'revoked';
  // ⚠️ An explicit null check, never `<=` on a null: `null <= now` is TRUE in
  // JavaScript (null coerces to 0), which would report every never-expiring
  // link as expired in 1970.
  if (report.expiresAtMs !== null && report.expiresAtMs <= nowMs) return 'expired';
  return 'ok';
}

/** A token is 32 URL-safe alphanumerics. Anything else is not_found, unlooked-up. */
export function isValidHostedReportToken(token: string): boolean {
  return token.length === HOSTED_REPORT_TOKEN_LENGTH && /^[A-Za-z0-9]+$/.test(token);
}

export type UploadValidation = { ok: true } | { ok: false; message: string };

/**
 * ⚠️ v1 accepts a SINGLE SELF-CONTAINED .html FILE ONLY — no zip bundles, no
 * folder uploads. Tool-generated reports are one file; a bundle needs a
 * different storage + serving shape (relative asset URLs inside a sandboxed
 * document), so accepting one "just in case" would half-work.
 *
 * Extension AND content type are both checked: an OS can report a surprising
 * type for .html, and a browser will happily hand us text/html for a file the
 * user renamed. Requiring both agreeing keeps the accepted set honest.
 */
export function validateHtmlUpload(input: {
  filename: string;
  contentType?: string | null;
  byteLength: number;
}): UploadValidation {
  const name = input.filename.trim().toLowerCase();
  if (!name.endsWith('.html') && !name.endsWith('.htm')) {
    return {
      ok: false,
      message: 'Only a single self-contained .html file can be hosted. Zip bundles and multi-file reports are not supported.',
    };
  }
  const type = (input.contentType ?? '').split(';')[0]!.trim().toLowerCase();
  // An empty type is tolerated — some browsers report '' for .htm on Windows,
  // and the extension check above has already run. A type that is present and
  // ISN'T html is a real mismatch and is rejected.
  if (type !== '' && type !== 'text/html') {
    return { ok: false, message: `Expected an HTML file, got "${type}".` };
  }
  if (input.byteLength <= 0) {
    return { ok: false, message: 'That file is empty.' };
  }
  if (input.byteLength > HOSTED_REPORT_MAX_BYTES) {
    return {
      ok: false,
      message: `That file is ${formatMb(input.byteLength)} — the limit is ${formatMb(HOSTED_REPORT_MAX_BYTES)}.`,
    };
  }
  return { ok: true };
}

export function formatMb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** The public path a client opens. Kept short — it gets pasted into emails. */
export function hostedReportPath(token: string): string {
  return `/r/${token}`;
}

export function buildHostedReportUrl(baseUrl: string, token: string): string {
  return `${baseUrl.replace(/\/+$/, '')}${hostedReportPath(token)}`;
}

/** Default expiry as a `yyyy-mm-dd` string, N days from `nowMs` in LOCAL time. */
export function defaultExpiryDateIso(nowMs: number, days: number): string {
  const d = new Date(nowMs + days * 24 * 60 * 60 * 1000);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * A `yyyy-mm-dd` expiry date → the epoch ms it expires AT: the END of that day
 * in LOCAL time (23:59:59.999). The UI says "expires on <date>", so the link
 * must survive the whole of that date rather than dying at midnight as it
 * begins — which is what a naive Date.parse of the bare date would do (it
 * parses as UTC midnight, i.e. potentially the previous day locally).
 */
export function expiryDateToMillis(dateIso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateIso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const end = new Date(y, mo - 1, d, 23, 59, 59, 999);
  // Reject a rolled-over date (2026-02-31 → 3 March) rather than silently
  // accepting a different day than the one the user picked.
  if (end.getFullYear() !== y || end.getMonth() !== mo - 1 || end.getDate() !== d) {
    return null;
  }
  return end.getTime();
}

// ---------- the download filename (Tools → HTML Hosting, staff download) ----------

/**
 * How long the name part may run before `.html`. Long enough that a real title
 * (`MTGM-Meta Ads Dashboard-15 - 22 Sep 26`) survives whole, short enough that
 * the header stays small and no filesystem complains.
 */
export const HOSTED_REPORT_FILENAME_MAX = 80;

/**
 * A report TITLE → a safe single-segment filename for the staff download.
 *
 * ⚠️⚠️ THE TITLE IS UNTRUSTED INPUT IN A RESPONSE HEADER. Titles are typed by
 * staff and go straight into `Content-Disposition`, so a newline or a quote in
 * one is HEADER INJECTION — a `"` closes the quoted filename, and a CR/LF ends
 * the header and starts whatever follows it. The defence is an ALLOW-LIST
 * (letters, digits, space, dot, dash, underscore) rather than a deny-list of
 * the characters we happened to think of: everything else becomes a space, so
 * there is nothing left that could mean anything to a header parser or a path.
 *
 * ⚠️ It is therefore LOSSY BY DESIGN — an accented client name or a `&` is
 * stripped rather than escaped. A slightly plainer filename is the right trade
 * against a second encoding scheme to get wrong.
 *
 * ⚠️ SHARED by the server (which writes the header) and the client (which sets
 * `a.download`), so the two cannot disagree about what a report is called.
 *
 * ⚠️ It also sidesteps the Windows device names (see WINDOWS_RESERVED_NAME) —
 * a DEFECT found while building this, not a feature: `NUL.html` cannot be
 * created on the OS the whole team runs on, so that download would just fail.
 *
 * @param title  the report's user-authored title
 * @param reportId  the fallback when NOTHING survives sanitisation — a title of
 *   only symbols (`★★★`) or only dots must not produce an empty, dot-only or
 *   hidden filename.
 */
export function hostedReportDownloadFilename(title: string, reportId: string): string {
  const base =
    sanitiseFilenameBase(title) ||
    sanitiseFilenameBase(reportId) ||
    // Unreachable with a Firestore auto-id (20 alphanumerics, which always
    // survive); kept because this function's contract is "always returns a safe
    // name", and a caller passing an empty id must not get back bare ".html".
    'report';
  return `${base}.html`;
}

function sanitiseFilenameBase(raw: string): string {
  let out = raw
    // The allow-list. A path separator, a quote, a CR/LF and every other
    // character all collapse to a space here — which is also why `../..` can
    // only ever become `.. ..`: one segment, no separators.
    .replace(/[^A-Za-z0-9 ._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // A LEADING dot makes a hidden file on Unix; a TRAILING dot or space is
  // silently trimmed by Windows, so the saved file wouldn't match the name we
  // said it would have. Stripping both is also what stops `...` surviving as a
  // dot-only name.
  out = stripEdgeDots(out);
  // ⚠️ Applied BEFORE the cap, so the cap stays honest about the final length.
  // Safe in that order: the prefix begins "report-", so no truncation of a
  // prefixed name can land back on a device name.
  if (isWindowsReservedName(out)) out = `report-${out}`;
  if (out.length > HOSTED_REPORT_FILENAME_MAX) {
    // Re-strip: the cut can land immediately after a dot or a space.
    out = stripEdgeDots(out.slice(0, HOSTED_REPORT_FILENAME_MAX));
  }
  return out;
}

/**
 * Windows device names, which are reserved WHATEVER THE EXTENSION — `NUL.html`
 * is exactly as uncreatable as `NUL` — so a report titled "CON" or "AUX" would
 * produce a download that simply fails, on the OS this whole team runs.
 *
 * ⚠️ MATCHED ON THE SEGMENT BEFORE THE FIRST DOT, which is where Windows looks:
 * `CON.v2.html` is reserved and `Report.CON.html` is not. Case-insensitive, and
 * trimmed first, because Windows ignores both.
 *
 * ⚠️ WHOLE SEGMENT, NOT A PREFIX — "CONSOLE" and "NULL" are ordinary words and
 * must not be mangled. That is what the anchors are for.
 */
const WINDOWS_RESERVED_NAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;

function isWindowsReservedName(base: string): boolean {
  return WINDOWS_RESERVED_NAME.test((base.split('.')[0] ?? '').trim());
}

function stripEdgeDots(s: string): string {
  return s.replace(/^[.\s]+/, '').replace(/[.\s]+$/, '');
}
